import { useFormContext, get } from "react-hook-form";
import { cn } from "@/utils/cn";
import { cva } from "class-variance-authority";

const formVariants = cva(
    "sm:text-md/6 block w-full min-w-0 rounded-3xl bg-white py-3 text-base outline-2 -outline-offset-1 focus:outline-2 focus:-outline-offset-2 disabled:opacity-50",
    {
        variants: {
            variant: {
                primary:
                    "text-darkgreen-500 shadow-darkgreen-200/25 outline-neutral-200 placeholder:text-darkgreen-500/75 focus:outline-darkgreen-500/50",
                success:
                    "text-lime-700 shadow-lime-500/25 outline-lime-500 placeholder:text-lime-700/75 focus:outline-lime-600",
                danger: "text-red-400 shadow-red-500/25 outline-red-500 placeholder:text-red-400/75 focus:outline-red-600",
            },
            size: {
                default: "font-base rounded-3xl px-5 py-3 text-base",
                sm: "font-sm rounded-3xl px-4 py-2 text-sm",
                lg: "font-lg rounded-3xl px-8 py-4 text-lg",
            },
            floatingLabel: {
                true: "rounded-4xl pt-8 pb-4 pl-14!",
                false: "",
            },
        },
        compoundVariants: [
            {
                size: "default",
                iconPosition: "left",
                className: "pl-12",
            },
            {
                size: "sm",
                iconPosition: "left",
                className: "pl-10",
            },
            {
                size: "lg",
                iconPosition: "left",
                className: "pl-14",
            },

            {
                size: "default",
                iconPosition: "right",
                className: "pr-12",
            },
            {
                size: "sm",
                iconPosition: "right",
                className: "pr-10",
            },
            {
                size: "lg",
                iconPosition: "right",
                className: "pr-14",
            },
            {
                variant: ["primary", "success", "danger"],
                type: "file",
                className:
                    "file:text-md p-0 file:mr-5 file:border-0 file:px-4 file:py-3 file:font-semibold hover:cursor-pointer hover:file:cursor-pointer",
            },
            {
                variant: ["primary"],
                type: "file",
                className:
                    "file:bg-darkgreen-500 file:text-white hover:outline-darkgreen-500 hover:file:bg-darkgreen-400",
            },
            {
                variant: ["success"],
                type: "file",
                className:
                    "file:bg-lime-500 file:text-white hover:outline-lime-600 hover:file:bg-lime-600",
            },
            {
                variant: ["danger"],
                type: "file",
                className:
                    "file:bg-red-500 file:text-white hover:outline-red-600 hover:file:bg-red-600",
            },
        ],
        defaultVariants: {
            variant: "primary",
            size: "default",
            iconPosition: "none",
            floatingLabel: "false",
        },
    },
);

const labelVariants = cva("mb-1 block w-full text-sm/6 font-medium", {
    variants: {
        variant: {
            primary: "text-darkgreen-500",
            success: "text-lime-700",
            danger: "text-red-400",
        },
        floatingLabel: {
            true: "pointer-events-none absolute z-1 mb-0 w-fit pt-2 pl-14 text-[0.6875rem]/6 tracking-widest",
            false: "",
        },
    },
    compoundVariants: [
        {
            variant: ["primary", "success", "danger"],
            hideLabel: true,
            className: "sr-only w-fit",
        },
    ],
    defaultVariants: {
        variant: "primary",
        hideLabel: false,
        floatingLabel: "false",
    },
});

const iconVariants = cva(
    "peer pointer-events-none absolute top-1/2 shrink-0 -translate-y-1/2 stroke-2",
    {
        variants: {
            variant: {
                primary: "text-darkgreen-500",
                success: "text-lime-700",
                danger: "text-red-400",
            },
            size: {
                default: "size-5",
                sm: "size-4",
                lg: "size-6",
            },
            iconPosition: {
                left: "left-5",
                right: "right-5",
            },
            disabled: {
                true: "opacity-50",
            },
        },
        defaultVariants: {
            variant: "primary",
            size: "default",
            iconPosition: "none",
        },
    },
);

export const Input = ({
    label,
    type,
    name,
    placeholder,
    value,
    className,
    variant,
    size,
    disabled,
    hideLabel,
    icon: Icon,
    iconPosition,
    floatingLabel = false,
    ...props
}) => {
    const {
        register,
        formState: { errors /*isSubmitted*/ },
    } = useFormContext();

    const hasError = get(errors, name);
    const formStateVariant = hasError
        ? "danger"
        : /*: isSubmitted
          ? "success"*/
          variant || "primary";

    const iconDefaultPosition = Icon ? (iconPosition ?? "left") : undefined;

    return (
        <>
            {type != "hidden" && (
                <label
                    htmlFor={name}
                    className={cn(
                        labelVariants({
                            variant: formStateVariant,
                            hideLabel,
                            floatingLabel,
                        }),
                    )}
                >
                    {label}
                </label>
            )}
            <div className={cn("relative flex", type == "hidden" && "hidden")}>
                {Icon && (
                    <Icon
                        className={cn(
                            iconVariants({
                                variant: formStateVariant,
                                size,
                                iconPosition: iconDefaultPosition,
                                disabled: disabled,
                            }),
                        )}
                    />
                )}
                <input
                    id={name}
                    type={type}
                    placeholder={placeholder}
                    value={value}
                    disabled={disabled}
                    className={cn(
                        formVariants({
                            className,
                            variant: formStateVariant,
                            size,
                            type,
                            iconPosition: iconDefaultPosition,
                            floatingLabel,
                        }),
                    )}
                    {...register(name)}
                    aria-invalid={hasError ? "true" : "false"}
                    {...props}
                />
            </div>
            {hasError && (
                <div className="mt-2">
                    <InputError
                        message={hasError.message}
                        key={hasError.message}
                    />
                </div>
            )}
        </>
    );
};

const InputError = ({ message }) => {
    return (
        <div className="px-4">
            <span role="alert" className="text-red-400">
                {message}
            </span>
        </div>
    );
};
